import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { type ClipboardScorePolicy } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { FullScreenDialog } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { type EnabledEmergencyCreditsConfig } from "@src/appV2/redesign/ClipboardScore/hooks/useEmergencyCreditsEducation";
import { Button } from "@src/appV2/redesign/components/Button";
import { ProgressBars } from "@src/appV2/redesign/components/ProgressBars";
import { TopRightCloseButton } from "@src/appV2/redesign/components/TopRightCloseButton";
import type { UseModalState } from "@src/appV2/redesign/system/ui-components";
import { getSafeAreaPaddings } from "@src/appV2/redesign/utils/getSafeAreaPaddings";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { type ReactNode, useState } from "react";

import { EarningCreditsStep } from "./EarningCreditsStep";
import { IntroStep } from "./IntroStep";
import { OneCreditEachStep } from "./OneCreditEachStep";
import { ScheduleChangesStep } from "./ScheduleChangesStep";

interface EmergencyCreditsEducationContainerProps {
  readonly modalState: UseModalState;
  readonly markAsSeen: () => void;
  readonly showCloseButton: boolean;
  readonly emergencyCreditsConfig: EnabledEmergencyCreditsConfig;
  readonly policy?: ClipboardScorePolicy;
}

interface SlideProps {
  readonly content: ReactNode;
  readonly scrollable?: boolean;
}

function Slide(props: SlideProps) {
  const { content, scrollable = false } = props;

  return (
    <Stack
      alignItems="center"
      justifyContent={scrollable ? "flex-start" : "center"}
      textAlign="center"
      spacing={5}
      overflow="auto"
      flex={1}
      minHeight={0}
      display="flex"
      flexDirection="column"
    >
      {content}
    </Stack>
  );
}

export function EmergencyCreditsEducationContainer(props: EmergencyCreditsEducationContainerProps) {
  const { modalState, markAsSeen, showCloseButton, emergencyCreditsConfig, policy } = props;
  const { paddingTop: safeAreaTop, paddingBottom: safeAreaBottom } = getSafeAreaPaddings({
    top: true,
    bottom: true,
  });
  const [step, setStep] = useState(0);
  const totalSteps = isDefined(policy) ? 4 : 3;
  const { userId: workerId } = useDefinedWorker();

  useLogEffect(APP_V2_USER_EVENTS.EMERGENCY_CREDITS_EDUCATION_STARTED, { workerId });

  const handleExit = () => {
    logEvent(APP_V2_USER_EVENTS.EMERGENCY_CREDITS_EDUCATION_EXITED, { workerId, step });
    modalState.closeModal();
    setStep(0);
  };

  const handleBack = () => {
    if (step === 0) {
      handleExit();
    } else {
      const previousStep = step - 1;
      const shouldSkipStep2 = previousStep === 2 && !isDefined(policy);
      const adjustedPrevious = shouldSkipStep2 ? 1 : previousStep;
      logEvent(APP_V2_USER_EVENTS.EMERGENCY_CREDITS_EDUCATION_STEP_VIEWED, {
        workerId,
        step: adjustedPrevious,
        action: "back",
      });
      setStep(adjustedPrevious);
    }
  };

  const handleNext = () => {
    const nextStep = step + 1;
    const shouldSkipStep2 = nextStep === 2 && !isDefined(policy);
    const adjustedNext = shouldSkipStep2 ? 3 : nextStep;

    if (adjustedNext < 4) {
      logEvent(APP_V2_USER_EVENTS.EMERGENCY_CREDITS_EDUCATION_STEP_VIEWED, {
        workerId,
        step: adjustedNext,
        action: "next",
      });
      setStep(adjustedNext);
    } else {
      logEvent(APP_V2_USER_EVENTS.EMERGENCY_CREDITS_EDUCATION_COMPLETED, { workerId });
      markAsSeen();
      handleExit();
    }
  };

  return (
    <FullScreenDialog modalState={modalState}>
      <Box
        sx={{
          height: "100%",
          position: "relative",
          paddingTop: safeAreaTop,
          paddingBottom: `calc(16px + ${safeAreaBottom})`,
        }}
      >
        {showCloseButton && <TopRightCloseButton onClick={handleExit} />}
        <Stack
          spacing={6}
          alignItems="center"
          sx={{
            width: "100%",
            height: "100%",
            justifyContent: "space-between",
            paddingX: 5,
            paddingTop: 8,
          }}
        >
          {step === 0 && <Slide content={<IntroStep />} />}
          {step === 1 && <Slide content={<OneCreditEachStep />} />}
          {step === 2 && policy && <Slide content={<ScheduleChangesStep policy={policy} />} />}
          {step === 3 && (
            <Slide
              content={<EarningCreditsStep emergencyCreditsConfig={emergencyCreditsConfig} />}
            />
          )}

          <ProgressBars
            currentStep={isDefined(policy) || step < 3 ? step : step - 1}
            totalSteps={totalSteps}
          />
          {step === 0 ? (
            <Button
              fullWidth
              size="large"
              variant="contained"
              sx={{ borderRadius: 999, height: 56 }}
              onClick={handleNext}
            >
              Next
            </Button>
          ) : (
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
              sx={{ width: "100%", maxWidth: 500, margin: "0 auto" }}
            >
              <Button
                size="large"
                variant="outlined"
                sx={{ borderRadius: 999, height: 56, flex: 1, maxWidth: 220 }}
                onClick={handleBack}
              >
                Back
              </Button>
              <Button
                size="large"
                variant="contained"
                sx={{ borderRadius: 999, height: 56, flex: 1, maxWidth: 220 }}
                onClick={handleNext}
              >
                {step < totalSteps - 1 ? "Next" : "Finish"}
              </Button>
            </Stack>
          )}
        </Stack>
      </Box>
    </FullScreenDialog>
  );
}
